//  AppSumo signups run on the Python backend regardless of which host served
// the app: the deal's tiers, credit pools and per-call deduction are built
// there, and an AppSumo agent created on Node would have no way to bill against
// those pools.
//
// Read from storage rather than the URL because this is called on every screen,
// long after the UTM query string is gone:
//   - appsumo_tier_level, set once the code is redeemed — the durable answer.
//   - utm_data, written at signup — covers the window between landing and
//     redeeming, and the user who skips redemption entirely.
// Exported because agent creation needs it too: an AppSumo signup on the India
// site keeps its own industry role, while everyone else there gets the Sales
// Receptionist. hasRedeemedAppSumo() is deliberately NOT the check to use for
// that — it is true only after a code is redeemed, so a buyer who has landed but
// not yet redeemed would be misread as an ordinary visitor.

export const isAppSumoUser = () => {
  try {
    if (localStorage.getItem("appsumo_tier_level")) return true;
    const utm = JSON.parse(localStorage.getItem("utm_data") || "null");
    const source = String(utm?.utm_source || "")
      .trim()
      .toLowerCase();
    return ["appsumo", "sumo", "app-sumo"].includes(source);
  } catch {
    // Unreadable storage is not an AppSumo signal — fall through to the host
    // checks rather than guessing.
    return false;
  }
};


/**
 * Has this account actually redeemed an AppSumo code?
 *
 * Deliberately stricter than isAppSumoUser above, which also trusts the signup
 * UTM. A UTM only says where someone arrived from — it is enough to decide
 * which backend serves them, but not enough to waive a paywall: a visitor who
 * followed an AppSumo link and never redeemed a code has paid nothing. Only
 * appsumo_tier_level, written when the redemption succeeds, proves payment.
 */
export const hasRedeemedAppSumo = () => {
  try {
    return !!localStorage.getItem("appsumo_tier_level");
  } catch {
    return false;
  }
};

// Set when a referral link is validated, from the server's answer to "is the
// owner of this link an AppSumo partner?". The client cannot work that out for
// itself — a referral code says nothing about who issued it.
const REFERRER_APPSUMO_KEY = "referrerIsAppSumo";

/**
 * Record whether the referral link this visitor arrived on belongs to an
 * AppSumo partner. Called by the screens that validate a referral code
 * (Onboarding, Start, RedirectHandler) with the server's response.
 */
export const setReferrerAppSumoFlag = (isAppSumo) => {
  try {
    if (isAppSumo) sessionStorage.setItem(REFERRER_APPSUMO_KEY, "true");
    else sessionStorage.removeItem(REFERRER_APPSUMO_KEY);
  } catch {
    /* storage blocked — fall back to the Node backend, which still works */
  }
};

// Someone who arrived on an AppSumo partner's referral link. They are NOT an
// AppSumo buyer themselves — they are an ordinary retail customer who will pay
// through Stripe like anyone else, and who starts on the free allowance — but
// their agent belongs on the Python backend all the same, because the plan
// they eventually buy is what funds their referrer's monthly minutes, and that
// accounting lives on the Python side.
//
// A referral from an ordinary user does NOT route here: nothing about that
// signup touches the AppSumo credit pools.
const isReferredByAppSumoPartner = () => {
  try {
    return sessionStorage.getItem(REFERRER_APPSUMO_KEY) === "true";
  } catch {
    return false;
  }
};

/**
 * REACT_APP_ENGLISH_MODE=true — the one switch that puts a non-production host
 * on the full .ai (US/CA) flow.
 *
 * Read by BOTH isIndiaSite and isPythonBackend below, deliberately. Flipping
 * only one of them leaves the half-switched state: a Python backend behind a
 * US/CA UI, or an India UI that can no longer buy a Vobiz number. One flag
 * cannot drift out of sync with itself; two flags can.
 *
 * This is lllwhy a staging host is turned off here rather than by deleting it from
 * the host lists — the env var is set per deployment and cannot be committed
 * into production by accident.
 *
 * CRA bakes REACT_APP_* in at BUILD time, so setting it requires a redeploy;
 * changing it on a running deployment does nothing until the next build.
 */
const isEnglishModeForced = () => process.env.REACT_APP_ENGLISH_MODE === "true";

export const isIndiaSite = () => {
  // TEMP OVERRIDE — uncomment to force the .ai (US/CA) flow on a host that
  // would otherwise be treated as India. Turns off: Hindi/English-India only
  // language list, the India plans screen, the +91 phone default and Vobiz
  // number buying. Pair it with the same line in isPythonBackend() below —
  // this one alone leaves the app on the Python backend.
  //
  // Prefer REACT_APP_ENGLISH_MODE=true where you can set env vars: it does the
  // same thing without editing code that could be committed by accident.
  // return false;

  if (typeof window === "undefined") return false;

  // Checked before the host, so the flag also covers app.rexpt.in itself. On
  // production that variable is simply never set.
  if (isEnglishModeForced()) return false;

  const host = (window.location.hostname || "").toLowerCase();

  if (host.endsWith(".in")) return true;

  const isDevHost =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.includes("truet.net") ||
    host.includes("rexptin.vercel.app")||
    host.includes("rexpt-dev-server-testing.vercel.app")||
    host.includes("rexpt-dev-server-main.vercel.app");


  if (isDevHost) return true;
  return false;
};
export const isPythonBackend = () => {
  // TEMP OVERRIDE — uncomment to force every request onto the Node backend,
  // i.e. the .ai flow. Use together with the same line in isIndiaSite() above;
  // on its own the backend flips to Node while the UI stays India-flavoured
  // (Hindi list, India plans screen, +91), which is the half-switched state.
  //
  // Do NOT commit this uncommented: it would disable the Python backend for
  // app.rexpt.in and for every AppSumo user, whose agents only exist there.
  // return false;

  if (typeof window === "undefined") return false;
  const host = (window.location.hostname || "").toLowerCase();
  const port = (window.location.port || "").toString();

  // AppSumo users always run on the Python backend.
  //
  // Checked FIRST, ahead of REACT_APP_ENGLISH_MODE below, and that order is
  // load-bearing: an AppSumo agent exists only on the Python side, so sending
  // one of these accounts to Node would leave it unable to find its own agent
  // or bill against its credit pools. English mode is about which flow a host
  // shows, never about breaking an account that has already paid.
  if (isAppSumoUser() || isReferredByAppSumoPartner()) return true;

  // Explicit env override if set
  if (process.env.REACT_APP_PY_BACKEND === "true") return true;

  // The same switch isIndiaSite reads — one flag drives both, so a host cannot
  // end up half-switched (Python backend behind a US/CA UI).
  if (isEnglishModeForced()) return false;

  // Production and live India domains
  //abc
  // rexptin.vercel.app is the staging frontend (backed by rex-bk.truet.net).
  // It is listed so staging exercises the Python backend. The Go Live flow
  // variant is selected separately in lib/experiments.js.
  //
  // Matched exactly, not with includes(): "rexpt-dev-server-testing1" does NOT
  // contain "rexpt-dev-server-testing.vercel.app" (the digit breaks the
  // substring), andkkk that is deliberate — testing1 is the US/CA brand and stays
  // on Node/variant A. A loose match here would quietly flip it.
  return (
    host === "app.rexpt.in" ||
    host === "rexptin.vercel.app" ||
    host.includes("rexpt-dev-server-testing.vercel.app") ||
    host.includes("rexpt-dev-server-main.vercel.app") ||
    // port === "3000" ||
    isAppSumoUser() ||
    isReferredByAppSumoPartner()
  );
};



/**
 * Should numbers be bought from Vobiz instead of Telnyx?
 *
 * Telnyx carries no India inventory at all — that is why every number picker
 * hard-blocks anything outside US/CA. The India site / Python backend needs Indian numbers, so
 * it buys from Vobiz, the carrier that already carries those agents' calls.
 *
 * AppSumo users are US/Canada customers on rexpt.us who buy Telnyx numbers,
 * so they are excluded from Vobiz.
 * For all other Python Backend / India visitors, Vobiz (+91 Indian numbers) is used.
 */
export const usesVobiz = () => {
  if (isAppSumoUser() || isReferredByAppSumoPartner()) {
    return false;
  }
  return isPythonBackend() || isIndiaSite();
};

/**
 * Provider path segment for the number endpoints — "vobiz" or "telnyx".
 *
 * The two routers expose the same paths with the same payloads
 * (available-numbers, create-number-order, purchase-number), so switching
 * provider really is just this segment.
 */
export const numberProviderPath = () => (usesVobiz() ? "vobiz" : "telnyx");

/**
 * Countries the CURRENT site can actually sell a number in.
 *
 * Neither carrier covers the other's market — Telnyx has no India stock and
 * Vobiz has no US stock — so this is genuinely one list or the other, never
 * the union. The pickers block anything outside it before searching, because
 * an empty result reads as "we're broken" while a clear message does not.
 */
export const supportedNumberCountries = () => (usesVobiz() ? ["IN"] : ["US", "CA"]);

export const isSupportedNumberCountry = (code) =>
  supportedNumberCountries().includes(String(code || "").toUpperCase());



/**
 * The location every India number search starts from.
 *
 * Vobiz answers a search for any Indian city, but the series it returns follows
 * the city — Mohali gives +9179…, Bangalore +9180… — so searching the owner's
 * own town handed each screen a different set of numbers. One pool, one
 * starting point, and every purchase screen offers the same thing.
 *
 * Lives here rather than in a component because it was being redefined per
 * screen, which is how AssignNumber ended up searching the business city while
 * the others defaulted to Bangalore.
 */
export const IN_DEFAULT_LOCATION = { city: "Bangalore", stateCode: "KA" };

/** Country / city / state a number search should use on the current site. */
export const numberSearchLocation = ({ country, city, stateCode } = {}) =>
  usesVobiz()
    ? { country: "IN", city: IN_DEFAULT_LOCATION.city, stateCode: IN_DEFAULT_LOCATION.stateCode }
    : { country: country || "US", city: city || "", stateCode: stateCode || "" };

export const unsupportedNumberCountryMessage = () =>
  usesVobiz()
    ? "Only India numbers are available to assign right now. Please pick an Indian location."
    : "Only US/Canada numbers are available to assign right now. Please pick a US or Canada location.";
const PY_BASE = () => process.env.REACT_APP_PY_API_BASE_URL || "";
const NODE_BASE = () => process.env.REACT_APP_API_BASE_URL || "";

/**
 * Ek endpoint ke liye sahi full URL do.
 * @param {string} pyPath   - Python path (leading slash ke saath), e.g. "/agents"
 * @param {string} nodePath - Node path   (leading slash ke saath), e.g. "/agentV2/createAgent"
 * @returns {string} full URL (jo backend applicable ho uska)
 *
 * Example:
 *   const url = pyOrNodeUrl("/agents", "/agentV2/createAgent");
 */
export const pyOrNodeUrl = (pyPath, nodePath) =>
  isPythonBackend() ? `${PY_BASE()}${pyPath}` : `${NODE_BASE()}${nodePath}`;

/**
 * Headers do. Python backend Bearer token maangta hai; Node ko zaroorat nahi.
 * @param {object} [extra] - koi extra headers (optional)
 * @returns {object} headers object (Content-Type + zaroorat ho to Authorization)
 *
 * Example:
 *   axios.post(url, body, { headers: authHeaders() })
 */
export const authHeaders = (extra = {}) => {
  const token =
    (typeof localStorage !== "undefined" && localStorage.getItem("token")) ||
    "";
  return {
    "Content-Type": "application/json",
    ...(isPythonBackend() && token ? { Authorization: `Bearer ${token}` } : {}),
    // ngrok free tunnel programmatic fetch() pe ek HTML warning page
    // (ERR_NGROK_6024, Content-Type: text/html) deta hai — ye header wo skip
    // karke seedha JSON deta hai (CORS error bhi isse hat jaata hai). Live
    // (rexpt.in, no ngrok) pe server ise ignore kar dega — safe hai.
    ...(isPythonBackend() ? { "ngrok-skip-browser-warning": "true" } : {}),
    ...extra,
  };
};

// //── Python agent details (GET /py/agents/{id}) ───────────────────────────────
// //Python se bana agent Node ke agentdetails table me nahi hota, is liye Node ke
// getAgentById/fetchAgentDetailById 404 dete hain. Ye helper Python se detail
// laata hai aur Node ke DONO response shapes me wrap karta hai
//   - { success, data: {...} }   (getAgentById jaisa)
//   - { agent: {...} }           (fetchAgentDetailById jaisa)
// taaki koi bhi component bina badle chal jaye.
// @param {string} agentId
// @returns {Promise<object>} normalized agent detail
export const fetchPythonAgentDetail = async (agentId) => {
  const res = await fetch(`${PY_BASE()}/agents/${agentId}`, {
    headers: authHeaders(),
  });
  const json = await res.json();
  const d = json?.data || {};
  const agent = {
    agent_id: d.agent_id || agentId,
    agentName: d.agentName,
    agentGender: d.agentGender,
    agentCode: d.agentCode,
    ...d,
  };
  // Node ke DONO endpoints ka shape ek saath match karo taaki koi component na
  // toote:
  //   getAgentById         → { success, data: <agent flat> }
  //   fetchAgentDetailById → { success, data: { agent, business, ... } }
  // Is liye `data` me flat agent bhi rakho AUR `data.agent` bhi, aur top-level
  // `agent` bhi. Jo component jis key ko padhe, mil jaye.
  // [FIX: python-call-summary] Agent Analysis card padhta hai
  // agentData.callSummary (Total Calls + 7-day Call Volume) aur
  // agentData.avgCallTime (Avg Call Duration). Python endpoint ab ye dono Node
  // se laa kar deta hai (Node hi calculate karta hai — numbers Node agent jaise
  // hi). Yahan pass-through kar do; na aaye to null (card 0/NA dikhata hai).
  return {
    success: true,
    agent,
    data: {
      ...agent,
      agent,
      business: d.business || null,
      callSummary: d.callSummary || null,
      avgCallTime: d.avgCallTime || null,
    },
  };
};

// ── Fish voices (GET /py/voices) ─────────────────────────────────────────────
// Voice picker Node ke /agent/list-voices se RETELL ki voices laata tha. Retell
// agent ke liye sahi, Python agent ke liye galat — Python Fish Audio se bolta
// hai aur Retell ki id ("11labs-Cimo") Fish ke liye bekaar hai.
//
// Fish ka response Retell wali SHAPE me convert kar ke lautaya jaata hai
// (voice_id / voice_name / gender / accent / preview_audio_url) taaki Step2.js
// ka poora rendering aur filtering bina badle chalta rahe.
//
// @param {object} [opts]
// @param {string} [opts.language]  - "ja-JP" ya "ja" — dono chalte hain
// @param {string} [opts.gender]    - "male" | "female"
// @param {number} [opts.pageSize]
// @returns {Promise<{data: Array}>} Retell-shape voice list
const LANGUAGE_LABELS = {
  en: "English",
  hi: "Hindi",
  ja: "Japanese",
  de: "German",
  fr: "French",
  es: "Spanish",
  it: "Italian",
  nl: "Dutch",
  da: "Danish",
  fi: "Finnish",
  no: "Norwegian",
  pt: "Portuguese",
  ru: "Russian",
  zh: "Chinese",
  ko: "Korean",
  ar: "Arabic",
};

const fetchFishPage = async ({ language, gender, pageSize }) => {
  const params = new URLSearchParams({ page_size: String(pageSize) });
  if (language) params.set("language", language);
  if (gender) params.set("gender", gender);

  const res = await fetch(`${PY_BASE()}/voices?${params.toString()}`, {
    headers: authHeaders(),
  });
  const json = await res.json();
  return json?.data || [];
};

export const getPythonVoices = async ({
  language,
  gender,
  pageSize = 30,
} = {}) => {
  // Gender na diya ho to dono alag-alag maango aur jodo. Ek hi bina-filter wali
  // call Fish ke apne order pe chalti hai aur aaram se saari ki saari female (ya
  // male) laa sakti hai — phir Step2 ka gender filter ek taraf khaali dikhata.
  const raw = gender
    ? await fetchFishPage({ language, gender, pageSize })
    : (
        await Promise.all([
          fetchFishPage({ language, gender: "female", pageSize }),
          fetchFishPage({ language, gender: "male", pageSize }),
        ])
      ).flat();

  const data = raw.map((v) => {
    const tags = v.tags || [];
    const lang = (v.languages || [])[0] || "";
    const languageLabel =
      LANGUAGE_LABELS[lang] || (lang ? lang.toUpperCase() : "Neutral");
    return {
      voice_id: v.voice_id,
      // Title jaisa Fish pe hai waisa hi — "Tokai Teio", "Mambo", "neel". Jin
      // voices ka naam Latin me nahi hai unhe server hi chhaant deta hai
      // (latin_only), is liye yahan kuch banane ki zaroorat nahi.
      voice_name: v.title || v.voice_id,
      // Step2 ka filter provider pe match karta hai (Retell list me
      // "elevenlabs" aata hai). Ye Fish ki voices hain, is liye "fish" — aur
      // wahan filter isPythonBackend() ke hisaab se provider chunta hai.
      provider: "fish",
      // Step2 `voice.gender === selectedGender` pe filter karta hai; Fish ise
      // tags me rakhta hai. Na mile to "female" — kyunki wahi Fish ka default
      // voice hai aur khaali gender wali entry list se hi gayab ho jaati.
      gender: tags.includes("male") ? "male" : "female",
      // UI "{accent} Accent" likhta hai. Fish accent nahi deta, language deta
      // hai — "English Accent" isse sabse kareeb aur padhne me theek hai.
      // Khaali chhoda to Step2 ka save-guard (voice_name && accent) fail ho
      // jaata hai aur chuni hui voice kabhi save hi nahi hoti.
      accent: languageLabel,
      // Server relative path deta hai ("/py/voices/<id>/preview?..."). PY_BASE
      // ke aakhir me "/py" pehle se hota hai, is liye use hata kar origin nikala
      // jaata hai — warna URL me "/py" do baar aa jaata.
      preview_audio_url: v.preview_url
        ? `${PY_BASE().replace(/\/py\/?$/, "")}${v.preview_url}`
        : "",
      description: v.description || "",
    };
  });

  return { data };
};

// ── Python voice test call (browser WebSocket, raw PCM audio) ─────────────────
// Node flow Retell Web SDK use karta hai. Python flow (isPythonBackend()) mein
// browser test call yahan se — ek WebSocket dono taraf raw PCM audio le jaata
// hai. Koi REST "start call" nahi; sirf agent_id chahiye.
//
//   Browser → server : Int16Array PCM16 mono @ 16000 Hz  (capture)
//   Server → browser : Int16Array PCM16 mono @ 24000 Hz  (playback)
//
// Capture aur playback alag rate pe → do alag AudioContext zaroori hain.
// WS URL .env ke Python base se banta hai (REACT_APP_PY_API_BASE_URL).
//
// Usage (component se):
//   const call = startPythonTestCall(agentId, { onStatus: setStatus });
//   ... later: call.hangUp();
//
// @param {string} agentId
// @param {object} [opts]
// @param {(status:string)=>void} [opts.onStatus] - status updates
//        ("requesting-mic" | "connecting" | "connected" | "ended" | "error")
// @returns {{ hangUp: () => void }}
export const startPythonTestCall = (agentId, opts = {}) => {
  const onStatus =
    typeof opts.onStatus === "function" ? opts.onStatus : () => {};
  const refs = {};

  (async () => {
    try {
      onStatus("requesting-mic");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });

      // Alag contexts: mic 16 kHz pe capture, agent 24 kHz pe bolta hai.
      const playCtx = new AudioContext({ sampleRate: 24000 });
      const captureCtx = new AudioContext({ sampleRate: 16000 });

      onStatus("connecting");
      // http(s)://.../py  →  ws(s)://.../py/ws/test/{agentId}
      const wsUrl =
        (process.env.REACT_APP_PY_API_BASE_URL || "").replace(/^http/, "ws") +
        `/ws/test/${agentId}`;
      const ws = new WebSocket(wsUrl);
      ws.binaryType = "arraybuffer";
      refs.ws = ws;

      // "connected" (End Call button) SIRF tab bhejo jab agent SACH me bolna
      // shuru kare — yani pehla audio chunk aaye — na ki socket khulne pe. Warna
      // button turant dikh jaata tha par agent 1-2s baad bolta tha, to user ko
      // lagta agent bol raha hai jabki silence hoti thi. Ab jab tak awaaz nahi,
      // UI "Connecting..." dikhata hai. [FIX: python-call-status-timing]
      //
      // Safety fallback: agar audio bilkul na aaye (agent chup / pipeline fail),
      // to button kabhi na aata aur user call cut hi na kar paata. Is liye 10s
      // ka timer — itni der me awaaz na aaye to bhi "connected" bhej do taaki
      // End Call button aa jaye. Jo pehle ho (audio ya timer).
      let firstAudioSeen = false;
      const markConnected = () => {
        if (firstAudioSeen || refs.stopped) return;
        firstAudioSeen = true;
        if (refs.connectFallback) {
          clearTimeout(refs.connectFallback);
          refs.connectFallback = null;
        }
        onStatus("connected");
      };
      refs.markConnected = markConnected;

      ws.onopen = () => {
        // Socket khula — abhi "connecting" hi (button "Connecting...").
        refs.connectFallback = setTimeout(markConnected, 10000);

        const source = captureCtx.createMediaStreamSource(stream);
        const node = captureCtx.createScriptProcessor(4096, 1, 1);

        node.onaudioprocess = (e) => {
          if (ws.readyState !== WebSocket.OPEN) return;
          const input = e.inputBuffer.getChannelData(0);
          const pcm = new Int16Array(input.length);
          for (let i = 0; i < input.length; i++) {
            pcm[i] = Math.max(-1, Math.min(1, input[i])) * 0x7fff;
          }
          ws.send(pcm.buffer);
        };

        source.connect(node);
        node.connect(captureCtx.destination);
        refs.node = node;
        refs.stream = stream;
      };

      // Har chunk pichhle ke baad queue karo, warna overlap ho ke audio
      // samajh nahi aati. Har scheduled source ko track bhi karte hain taaki
      // hang-up pe unhe turant band kiya ja sake (warna future me schedule ho
      // chuke chunks 1-2s tak bajte rehte the — "End Call" ke baad bhi agent
      // bolta tha). [FIX: python-call-audio-tail]
      let nextPlayTime = 0;
      const scheduledSources = new Set();
      refs.scheduledSources = scheduledSources;
      ws.onmessage = (e) => {
        if (refs.stopped) return; // hang-up ke baad naya audio schedule mat karo
        if (!(e.data instanceof ArrayBuffer)) return;

        // Pehla audio = agent bolna shuru → ab "connected" (End Call button aata
        // hai theek us waqt jab awaaz shuru hoti hai). [FIX: python-call-status-timing]
        markConnected();

        const pcm = new Int16Array(e.data);
        const f32 = new Float32Array(pcm.length);
        for (let i = 0; i < pcm.length; i++) f32[i] = pcm[i] / 0x7fff;

        const buffer = playCtx.createBuffer(1, f32.length, 24000);
        buffer.copyToChannel(f32, 0);

        const src = playCtx.createBufferSource();
        src.buffer = buffer;
        src.connect(playCtx.destination);

        const now = playCtx.currentTime;
        if (nextPlayTime < now) nextPlayTime = now + 0.05;
        src.start(nextPlayTime);
        nextPlayTime += buffer.duration;

        // Book-keeping so hang-up can stop what's still queued.
        scheduledSources.add(src);
        src.onended = () => scheduledSources.delete(src);
      };

      // The backend closes this socket when the agent ends the call (voice
      // "cut the call" -> worker.cancel()). The browser must then flip the
      // "End Call" button back — that happens via onStatus("ended") below.
      // Guard it so it fires EXACTLY once whether close or error comes first
      // (a server-side close can surface as onerror THEN onclose, or the audio
      // teardown can race) — otherwise the button sometimes stayed red.
      let endedSent = false;
      const sendEnded = (from) => {
        if (endedSent) return;
        endedSent = true;
        console.log("[pyCall] sendEnded fired from:", from, "→ onStatus('ended')");
        onStatus("ended");
      };
      ws.onerror = () => {
        // Treat an error as the call ending too (not just "error"), so the
        // button always resets — the record is already saved server-side.
        console.log("[pyCall] ws.onerror");
        sendEnded("onerror");
      };
      ws.onclose = () => {
        console.log("[pyCall] ws.onclose");
        sendEnded("onclose");
        stopPlayback(); // koi bhi bacha hua queued audio turant band
        refs.node?.disconnect();
        refs.stream?.getTracks().forEach((t) => t.stop());
      };

      // Sab queued/agla playback turant rok do aur contexts band kar do. hang-up
      // aur onclose DONO se aata hai, is liye `refs.stopped` guard se sirf ek
      // baar chalta hai — warna band ho chuke AudioContext pe dubara close()
      // "Cannot close a closed AudioContext" throw karta tha.
      // [FIX: python-call-audioctx-double-close]
      const stopPlayback = () => {
        if (refs.stopped) return; // already stopped — dubara mat chalao
        refs.stopped = true;
        // connect-fallback timer ab bekaar — call khatam. Warna call-end ke baad
        // fire ho ke ek stale "connected" bhej deta. [FIX: python-call-status-timing]
        if (refs.connectFallback) {
          clearTimeout(refs.connectFallback);
          refs.connectFallback = null;
        }
        for (const src of scheduledSources) {
          try {
            src.onended = null;
            src.stop();
            src.disconnect();
          } catch (_e) {
            /* pehle se ended/stopped — harmless */
          }
        }
        scheduledSources.clear();
        // close() ek Promise return karta hai jo already-closed pe REJECT hota
        // hai (sync try/catch use nahi hota) — is liye state check + .catch().
        if (playCtx.state !== "closed") playCtx.close().catch(() => {});
        if (captureCtx.state !== "closed") captureCtx.close().catch(() => {});
      };
      refs.stopPlayback = stopPlayback;
    } catch (err) {
      console.error("Python test call failed", err);
      onStatus("error");
    }
  })();

  // hang-up: pehle queued agent-audio turant band karo (warna 1-2s tak agent
  // bolta rehta tha), phir socket close. stopPlayback khud `refs.stopped` set
  // karta hai — is liye yahan pehle set NAHI karna (warna guard cleanup skip kar
  // deta). Agar stopPlayback abhi bana hi na ho (mic-permission se pehle click),
  // to `refs.stopped` manually set karo taaki baad me pehla audio na baje, aur
  // socket band — onclose baaki kar dega. [FIX: python-call-audio-tail]
  return {
    hangUp: () => {
      if (refs.stopPlayback) {
        refs.stopPlayback();
      } else {
        refs.stopped = true;
      }
      refs.ws?.close();
    },
  };
};
